<template>
    <div ref="countdown" v-show="show" class="tw-flex gap">
        <template v-if="variant === 'cards'">
            <!--    DAYS    -->
            <div v-if="!only_show_seconds_card" class='tw-flex tw-flex-col tw-items-center tw-content-between tw-text-2xl tw-rounded tw-text-white countdown-count'>
                {{ remaining_time.days }}
                <span class="tw-text-sm countdown-unit">{{ $t('general.days') }}</span>
            </div>

            <!--    HOURS    -->
            <div v-if="!only_show_seconds_card" class='tw-flex tw-flex-col tw-items-center tw-content-between tw-text-2xl tw-rounded tw-text-white countdown-count'>
                {{ remaining_time.hours }}
                <span class="tw-text-sm countdown-unit">{{ $t('general.hours') }}</span>
            </div>

            <!--    MINUTES    -->
            <div v-if="!only_show_seconds_card" class='tw-flex tw-flex-col tw-items-center tw-content-between tw-text-2xl tw-rounded tw-text-white countdown-count'>
                {{ remaining_time.minutes }}
                <span class="tw-text-sm countdown-unit">{{ $t('general.minutes') }}</span>
            </div>

            <!--    SECONDS    -->
            <div v-if="only_show_seconds_card" class='tw-flex tw-flex-col tw-items-center tw-content-between tw-text-2xl tw-rounded tw-text-white countdown-count'>
                {{ remaining_time.seconds }}
                <span class="tw-text-sm countdown-unit">sec</span>
            </div>
        </template>

        <template v-if="variant === 'string'">
            <span class="tw-font-bold tw-text-lg">
                {{ dayString }}
                {{ hourString }}
                {{ minuteString }}
                {{ secondsString }}
            </span>
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import Global from "../../mixins/Global";
    import DateMixin from "../../mixins/Date";

    export default {
        name: 'Countdown',

        mixins: [Global, DateMixin],

        props: {
            deadline: {
                required: true
            },

            variant: {
                default: 'cards',
                validator(value) {
                    return ['cards', 'string'].includes(value);
                }
            },

            disable_seconds: {
                default: false,
            },
        },

        computed: {
            only_show_seconds_card() {
                const time = this.remaining_time;
                return time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds >= 0;
            },

            dayString() {
                if (this.remaining_time.days <= 0) {
                    return '';
                }

                const unit = this.remaining_time.days === 1 ? this.$t('general.day') : this.$t('general.days');
                return `${this.remaining_time.days} ${unit.toLowerCase()}`;
            },

            hourString() {
                if (this.remaining_time.hours <= 0) {
                    return '';
                }

                const unit = this.settings.locale === 'en' ? this.$t('general.hours') : this.$t('general.hour');
                return `${this.remaining_time.hours} ${unit.toLowerCase()}`;
            },

            minuteString() {
                if (this.remaining_time.minutes <= 0) {
                    return '';
                }

                const unit = this.$t('general.minutes_abbreviation');
                return `${this.remaining_time.minutes} ${unit.toLowerCase()}`;
            },

            secondsString() {
                if (this.disable_seconds) {
                    return '';
                }

                const unit = this.$t('general.seconds_abbreviation');
                return `${this.remaining_time.seconds} ${unit.toLowerCase()}`;
            },
        },

        data() {
            return {
                remaining_time: {
                    total: 0,
                    days: 0,
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                },
                formatted_date: null,
                show: true,
                interval: null
            };
        },

        methods: {
            getRemainingTime(to_date) {
                const total = Date.parse(this.browserSafeDate(to_date)) - Date.parse(new Date());
                const seconds = Math.floor( (total / 1000) % 60 );
                const minutes = Math.floor((total / 1000 / 60) % 60);
                const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
                const days = Math.floor(total / (1000 * 60 * 60 * 24));

                if (total <= 0) {
                    return {
                        total: 0,
                        days: 0,
                        hours: 0,
                        minutes: 0,
                        seconds: 0,
                    }
                }

                if (isNaN(total) || isNaN(days) || isNaN(hours) || isNaN(minutes) || isNaN(seconds) ) {
                    this.show = false;
                    return;
                }

                return {
                    total,
                    days,
                    hours,
                    minutes,
                    seconds
                }
            },

            // Start date retrieved from API may be in different formats
            getFormattedDate(date, expected_format) {
                if (moment(date, 'MMMM DD, YYYY HH:mm', true).isValid()) {
                    return moment(this.deadline, 'MMMM DD, YYYY HH:mm').format(expected_format);
                }

                else if (moment(date, 'YYYY-MM-DD HH:mm', true).isValid()) {
                    return moment(this.deadline, 'YYYY-MM-DD HH:mm').format(expected_format);
                }

                else if (moment(date, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
                    return moment(this.deadline, 'YYYY-MM-DD HH:mm:ss').format(expected_format);
                }

                else {
                    return false;
                }
            },

            getIntervalTimeout() {
                switch (this.variant) {
                    case 'cards':
                        return 1000; // Cards variant only shows the dd/hh/mm, so unnecessary to run function every second.
                    case 'string':
                        return 1000;
                    default:
                        return 1000;
                }
            },

            initializeClock() {
                    const formatted_date = this.getFormattedDate(this.deadline, 'YYYY-MM-DD HH:mm:ss');
                    this.remaining_time = this.getRemainingTime(formatted_date);
                    let index = 0;

                    this.interval = setInterval(() => {
                        try {
                            index++;

                            this.remaining_time = this.getRemainingTime(formatted_date);

                            if(!this.show){
                                this.clearInterval(this.interval);
                            }

                            if(this.remaining_time.total <= 0 && index >= 2) { // We do a check on the index in case the clock gets initialized with a date that already has been passed (in this case the refresh page event would be emitted over and over.)
                                this.$emit('countdownFinished');
                            } else if (this.remaining_time.total <= 0) {
                                this.clearInterval()
                            }
                        } catch (e) {
                            if(this.interval) {
                                clearInterval(this.interval);
                            }
                        }
                    }, this.getIntervalTimeout());
            },
        },

        mounted() {
            try {
                this.initializeClock();
            } catch(e) {
                clearInterval(this.interval);
            }
        },

        beforeDestroy() {
            clearInterval(this.interval);
        }
    };
</script>

<style lang="scss" scoped>
    .gap {
        gap: 0.286rem;
    }

    .countdown-count {
        width: 4.571rem;
        height: 4.571rem;
        padding: 8px;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(50px);
        font-weight: 600;
    }

    .countdown-unit {
        font-weight: 300;
    }
</style>
